import { useCallback, useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import { useTranslation } from 'react-i18next';
import { ROUTE_DEFINITIONS } from '@/routes';
import { useUI } from '@/hooks/ui/useUI';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';
import { formatDateToYYYYMMDD } from '@/utils';
import { absenceService } from '@/api/domains/absence/absence.service';
import {
  ABSENCE_SCOPE_LIST_REFRESH_EVENT,
  getAbsenceTypeOptions,
  getAbsenceStatusOptions,
} from '@/api/domains/absence/absence.types';
import type {
  AbsenceScopeItem,
  AbsenceTotals,
  AbsenceType,
  AbsenceStatus,
  AbsenceSelectOption,
} from '@/api/domains/absence/absence.types';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table';
import { useEmployeeLookupSelector } from '@/hooks/employee/useEmployeeLookupSelector';
import { useAbsenceScopeExport } from '@/hooks/absence/useAbsenceScopeExport';
import type { UIExportFormat } from '@/types/fileExport.types';

// Tipos de ausentismo con panel de detalle implementado. Las demás tarjetas (vacaciones,
// permisos) llegan en tickets posteriores: basta sumar la entrada aquí cuando existan.
const MEDICAL_LEAVE_TYPE_CODE: AbsenceType = 'licencia';


export interface AbsenceScopeListFilters {
  from?: string;
  to?: string;
  tipoAusentismo: AbsenceType;
  estado: AbsenceStatus;
  employeeIds?: string[];
}

export interface UseAbsenceScopeListResult {
  filters: AbsenceScopeListFilters;
  setFilters: Dispatch<SetStateAction<AbsenceScopeListFilters>>;
  defaultFrom: string;
  defaultTo: string;
  filterDefinitions: Array<{
    key: string;
    label: string;
    options: AbsenceSelectOption[];
    defaultValue: string;
  }>;
  handleFilterChange: (key: string, value: string | string[]) => void;
  employeeSelectorProps: {
    options: Array<{ value: string; label: string }>;
    hasNextPage: boolean;
    isLoadingMore: boolean;
    onLoadMore: () => Promise<void>;
    onSearchChange: (value: string) => void;
  };
  handleEmployeeIdsChange: (value: string | string[]) => void;
  selectedEmployeeIds: string[];
  isRecalculationNoticeDismissed: boolean;
  dismissRecalculationNotice: () => void;
  data: AbsenceScopeItem[];
  totals: AbsenceTotals;
  totalRecords: number;
  isLoading: boolean;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  onSearch: (text: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  columns: TableColumn<AbsenceScopeItem>[];
  actions: TableAction<AbsenceScopeItem>[];
  exportAbsences: (format: UIExportFormat) => Promise<void>;
  isExporting: boolean;
}

export const useAbsenceScopeList = (): UseAbsenceScopeListResult => {
  const { t: translate } = useTranslation('absence');
  const { showApiError, openSidePanel } = useUI();

  const {
    options: employeeOptions,
    hasNextPage: employeeHasNextPage,
    isLoadingMore: employeeIsLoadingMore,
    onLoadMore: employeeOnLoadMore,
    onSearchChange: employeeOnSearchChange,
  } = useEmployeeLookupSelector();

  const defaultFrom = useMemo(() => {
    const fromDate = new Date();
    fromDate.setMonth(fromDate.getMonth() - 1);
    return formatDateToYYYYMMDD(fromDate);
  }, []);

  const defaultTo = useMemo(() => formatDateToYYYYMMDD(new Date()), []);

  const [isRecalculationNoticeDismissed, setIsRecalculationNoticeDismissed] = useState(false);
  const dismissRecalculationNotice = useCallback(() => {
    setIsRecalculationNoticeDismissed(true);
  }, []);

  const [totals, setTotals] = useState<AbsenceTotals>({
    licencias: 0,
    vacaciones: 0,
    permisosEspeciales: 0,
    permisosAdministrativos: 0,
    permisosFraccion: 0,
    faltas: 0,
  });

  const emptyTotals: AbsenceTotals = useMemo(
    () => ({
      licencias: 0,
      vacaciones: 0,
      permisosEspeciales: 0,
      permisosAdministrativos: 0,
      permisosFraccion: 0,
      faltas: 0,
    }),
    [],
  );

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<AbsenceScopeListFilters>,
      _signal: AbortSignal,
    ): Promise<PaginatedFetchResult<AbsenceScopeItem> | null> => {
      const fromDateString = params.filters.from ?? defaultFrom;
      const toDateString = params.filters.to ?? defaultTo;

      const employeeIdsParam = params.filters.employeeIds?.length
        ? params.filters.employeeIds.join(',')
        : null;

      const response = await absenceService.listByScope({
        startDate: fromDateString,
        endDate: toDateString,
        absenceType: params.filters.tipoAusentismo,
        status: params.filters.estado,
        employeeIds: employeeIdsParam,
        page: params.page,
        pageSize: params.pageSize,
        searchText: params.searchText ?? null,
        orderBy: params.sort?.key ?? null,
        orderDirection: params.sort?.direction ?? null,
      });

      if (!response.success || !response.data) {
        showApiError(response, translate('scope.errors.loadFailed'));
        setTotals(emptyTotals);
        return null;
      }

      setTotals(response.data.totals);
      return { items: response.data.data, totalRecords: response.data.totalRecords };
    },
    [defaultFrom, defaultTo, showApiError, translate, emptyTotals],
  );

  const {
    rows,
    total: totalRecords,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading: isLoading,
    onSearch,
    onSortChange,
    filters,
    setFilters,
    debouncedSearchText,
    refetch,
  } = usePaginatedList<AbsenceScopeItem, AbsenceScopeListFilters>({
    fetchFn,
    initialFilters: {
      from: formatDateToYYYYMMDD(new Date(new Date().setMonth(new Date().getMonth() - 1))),
      to: formatDateToYYYYMMDD(new Date()),
      tipoAusentismo: 'todos' as AbsenceType,
      estado: 'todos' as AbsenceStatus,
      employeeIds: [],
    },
    initialSort: { key: 'createdAt', direction: 'DESC' },
    orderByMap: {
      empleado: 'employeeName',
      rut: 'employeeTaxId',
      tipo: 'type',
      descripcion: 'type',
      fechaDesde: 'startDate',
      fechaHasta: 'endDate',
      estado: 'status',
      fechaCreacion: 'createdAt',
      creadoPor: 'createdBy',
    },
    screenCode: ROUTE_DEFINITIONS.GESTION_AUSENTISMOS.code,
    // `from`/`to` son una ventana relativa ("último mes a hoy"); si se restaura tal cual quedó
    // persistida se congela en la fecha absoluta de la última visita en vez de avanzar con "hoy".
    onHydrated: (hydrated) => ({ ...hydrated.filters, from: defaultFrom, to: defaultTo }),
  });

  const { exportAbsences, isExporting } = useAbsenceScopeExport({
    startDate: filters.from ?? defaultFrom,
    endDate: filters.to ?? defaultTo,
    absenceType: filters.tipoAusentismo,
    status: filters.estado,
    employeeIds: filters.employeeIds,
    searchText: debouncedSearchText,
  });

  // Refresca listado y cards de totales tras crear un ausentismo desde el sidebar.
  useEffect(() => {
    const handleRefresh = () => { refetch(); };
    window.addEventListener(ABSENCE_SCOPE_LIST_REFRESH_EVENT, handleRefresh);
    return () => {
      window.removeEventListener(ABSENCE_SCOPE_LIST_REFRESH_EVENT, handleRefresh);
    };
  }, [refetch]);

  const handleFilterChange = useCallback(
    (key: string, value: string | string[]) => {
      setFilters((prev) => ({ ...prev, [key]: value as string }));
    },
    [setFilters],
  );

  const handleEmployeeIdsChange = useCallback(
    (value: string | string[]) => {
      const ids = Array.isArray(value) ? value : value ? [value] : [];
      setFilters((prev) => ({ ...prev, employeeIds: ids }));
    },
    [setFilters],
  );

  const employeeSelectorProps = useMemo(
    () => ({
      options: employeeOptions,
      hasNextPage: employeeHasNextPage,
      isLoadingMore: employeeIsLoadingMore,
      onLoadMore: employeeOnLoadMore,
      onSearchChange: employeeOnSearchChange,
    }),
    [employeeOptions, employeeHasNextPage, employeeIsLoadingMore, employeeOnLoadMore,
      employeeOnSearchChange],
  );

  const typeOptions = useMemo(() => getAbsenceTypeOptions(translate), [translate]);
  const statusOptions = useMemo(() => getAbsenceStatusOptions(translate), [translate]);

  const filterDefinitions = useMemo(
    () => [
      {
        key: 'tipoAusentismo',
        label: translate('filters.type'),
        options: typeOptions,
        defaultValue: 'todos',
      },
      {
        key: 'estado',
        label: translate('filters.status'),
        options: statusOptions,
        defaultValue: 'todos',
      },
    ],
    [translate, typeOptions, statusOptions],
  );

  const columns: TableColumn<AbsenceScopeItem>[] = useMemo(
    () => [      
      {
        key: 'rut',
        header: translate('scope.table.columns.rut'),
        sortable: true,
        width: 80,
      },
      {
        key: 'empleado',
        header: translate('scope.table.columns.empleado'),
        sortable: true,
        width: 130,
      },
      {
        key: 'tipo',
        header: translate('table.columns.tipo'),
        sortable: true,
      },
      {
        key: 'descripcion',
        header: translate('table.columns.descripcion'),
        sortable: false,
      },
      {
        key: 'fechaCreacion',
        header: translate('table.columns.fechaCreacion'),
        sortable: true,
      },
      {
        key: 'fechaDesde',
        header: translate('table.columns.fechaDesde'),
        sortable: true,
      },
      {
        key: 'fechaHasta',
        header: translate('table.columns.fechaHasta'),
        sortable: true,
      },
      {
        key: 'estado',
        header: translate('table.columns.estado'),
        sortable: true,
      },
    ],
    [translate],
  );

  // No se oculta con `!canEdit`: el permiso que gobierna esta acción es "view" (abre un panel de
  // lectura), ya garantizado por el route guard de la pantalla. La capacidad de editar se protege
  // en el botón Guardar del propio panel — ocultar aquí haría inalcanzable el criterio de
  // aceptación "con permiso de solo lectura el panel se abre bloqueado".
  const actions: TableAction<AbsenceScopeItem>[] = useMemo(
    () => [
      {
        id: 'detail',
        label: translate('common:tableActions.detail'),
        icon: 'eye' as const,
        hidden: (row) => row.typeCode !== MEDICAL_LEAVE_TYPE_CODE || row.recordId === null,
        onClick: (row) => {
          openSidePanel({
            title: translate('medicalLeave.detail.sidePanel.title'),
            component: 'MedicalLeaveDetailPanel',
            props: { medicalLeaveId: row.recordId },
          });
        },
      },
    ],
    [translate, openSidePanel],
  );

  return {
    filters,
    setFilters,
    defaultFrom,
    defaultTo,
    filterDefinitions,
    handleFilterChange,
    employeeSelectorProps,
    handleEmployeeIdsChange,
    selectedEmployeeIds: filters.employeeIds ?? [],
    isRecalculationNoticeDismissed,
    dismissRecalculationNotice,
    data: rows,
    totals,
    totalRecords,
    isLoading,
    page,
    pageSize,
    setPage,
    setPageSize,
    onSearch,
    onSortChange,
    columns,
    actions,
    exportAbsences,
    isExporting,
  };
};
